import { cn } from "cn";
import { useState } from "react";
import { BwArrowUpRightMdLine } from "@/components/ui/icons";
import { Favicon } from "@/components/ui/icons/favicon";
import { messageCardClassName } from "./message-card";
/**
 * Only the fields this card renders, all optional — the component reads every
 * one with `?.`. Declaring the minimum instead of a concrete DTO lets both the
 * local zod type and the generated wire type satisfy it (they disagree on
 * whether nullable fields are also optional).
 */
export type LinkPreviewCardProps = {
  linkPreview?: {
    url?: string | null;
    domain?: string | null;
    title?: string | null;
    ogTitle?: string | null;
    ogImage?: string | null;
    description?: string | null;
  } | null;
  /** The containing bubble's spacing and surface for a card shell. Applied last. */
  className?: string;
};

/**
 * The origin `Favicon` can parse, or nothing. `Favicon` calls `new URL(uri)`
 * unguarded, so a preview the scraper stored with a malformed url would throw
 * out through the whole message list.
 */
function faviconOrigin(url?: string | null, domain?: string | null) {
  const candidate = url || (domain ? `https://${domain}` : "");

  if (!candidate) {
    return undefined;
  }

  try {
    return new URL(candidate).origin;
  } catch {
    return undefined;
  }
}

export function LinkPreviewCard({
  linkPreview,
  className,
}: LinkPreviewCardProps) {
  const [imageFailed, setImageFailed] = useState(false);

  const title = linkPreview?.title || linkPreview?.ogTitle;

  if (!title) {
    return null;
  }

  const domain = linkPreview?.domain ?? undefined;
  const description = linkPreview?.description ?? "";
  const favicon = faviconOrigin(linkPreview?.url, linkPreview?.domain);
  const ogImage = linkPreview?.ogImage;

  return (
    <a
      href={linkPreview?.url ?? undefined}
      target="_blank"
      rel="noreferrer"
      className={cn(
        /* sorted */
        messageCardClassName,
        "flex items-start gap-2",
        "p-3",
        className,
      )}
    >
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex items-center gap-1">
          {!!favicon && (
            <Favicon
              uri={favicon}
              size={64}
              width={20}
              height={20}
              className="size-5 shrink-0 rounded-sm"
            />
          )}

          <p
            className="flex-1 truncate text-sm font-medium text-text-dark-max"
            title={domain}
          >
            {domain}
          </p>

          <BwArrowUpRightMdLine
            size={16}
            className="shrink-0 text-text-dark-low"
          />
        </div>

        <p className="truncate text-sm text-text-primary" title={title}>
          {title}
        </p>

        {!!description && (
          <p
            className="line-clamp-2 text-xs text-text-dark-high"
            title={description}
          >
            {description}
          </p>
        )}
      </div>

      {!!ogImage && !imageFailed && (
        <img
          src={ogImage}
          alt=""
          loading="lazy"
          onError={() => setImageFailed(true)}
          className="size-18 shrink-0 rounded-sm border border-border-dark-min object-cover"
        />
      )}
    </a>
  );
}
